import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BreadcrumbItemProps } from '../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import CustomEmptyPage from '../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import PageCurvedContainer from '../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import { REWARDS_HISTORY_DETAILS_ROUTE } from '../../../shared/constants/auth';
import {
  DEFAULT_REWARDS_ORDER_HISTORY_FILTER,
  REWARDS_ORDER_STATUS_MAPPING,
  REWARDS_ORDER_TYPES,
  REWARDS_OUTGOINGS_ORDER_DETAILS
} from '../../../shared/constants/rewards';
import { CLICK_ACTION, ORDER_DETAIL } from '../../../shared/constants/tagManager';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { RewardsContext } from '../../../shared/context/rewards/rewardsContext';
import { useIsMounted } from '../../../shared/hooks/useIsMounted';
import { IRewardsOrder, IRewardsOrderFilters } from '../../../shared/interfaces/rewards';
import Rewards from '../../../shared/services/api/rewards/rewards';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_HISTORY } from '../../../shared/translations/rewards';
import { TRANSLATIONS_SETTINGS } from '../../../shared/translations/settings';
import { toLocaleNumberFormat } from '../../../shared/utils/utils';
import RewardsPageHeader from '../sections/RewardsPageHeader/RewardsPageHeader';
import RewardsHistoryFilter from './sections/RewardsHistoryFilter/RewardsHistoryFilter';
import RewardsHistoryTable from './sections/RewardsHistoryTable/RewardsHistoryTable';

const RewardsHistory = () => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<IRewardsOrder[] | undefined>(undefined);
  const [rewardsOrdersFilters, setRewardsOrdersFilters] = useState<IRewardsOrderFilters>(
    DEFAULT_REWARDS_ORDER_HISTORY_FILTER
  );

  const { company } = useContext(CompanyContext);
  const { balance, currency, loading: rewardsLoading, error: rewardsError } = useContext(RewardsContext);
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const loadBalanceBreadcrumbs: BreadcrumbItemProps[] = [
    { label: t(TRANSLATION_HISTORY.header.breadcrumb.rewards) },
    { label: t(TRANSLATION_HISTORY.header.breadcrumb.history) }
  ];

  const isValidCompany = useCallback(() => {
    return company.id !== '';
  }, [company]);

  const getRewardsOrdersList = async () => {
    if (isMounted && isValidCompany()) {
      try {
        setLoading(true);
        setOrders(undefined);
        setError(false);
        const response = await Rewards.getFilteredOrders(
          company.id,
          rewardsOrdersFilters.type.value,
          rewardsOrdersFilters.searchValue?.toLowerCase(),
          rewardsOrdersFilters.status.status
        );
        if (response.orders.length !== 0) {
          response.orders = setupOrders(response.orders);
          setOrders(response.orders);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const setupOrders = (ordersList: IRewardsOrder[]) => {
    return ordersList.map((order: IRewardsOrder) => {
      return {
        ...order,
        transactionDetail:
          rewardsOrdersFilters.type.value === REWARDS_ORDER_TYPES.deposits
            ? TRANSLATION_HISTORY.table.details.deposits
            : (REWARDS_OUTGOINGS_ORDER_DETAILS[order.transactionDetail] ??
              TRANSLATION_HISTORY.table.details.outgoings.points),
        status: REWARDS_ORDER_STATUS_MAPPING[rewardsOrdersFilters.type.value][order.status],
        amount: toLocaleNumberFormat(parseFloat(order.amount.toString()), { style: 'currency', currency: currency }),
        onClick: () => onClickOrder(order.id)
      };
    });
  };

  const changeRewardsOrdersFilters = (newFilters: IRewardsOrderFilters) => {
    setRewardsOrdersFilters(newFilters);
  };

  const onClickOrder = (orderId: string) => {
    TagManagerService.pushEvent(CLICK_ACTION, ORDER_DETAIL, rewardsOrdersFilters.type.gtmTag);
    navigate(REWARDS_HISTORY_DETAILS_ROUTE + `?orderId=${orderId}`);
  };

  useEffect(() => {
    getRewardsOrdersList();
  }, [rewardsOrdersFilters]);

  useEffect(() => {
    if (isMounted) {
      changeRewardsOrdersFilters(DEFAULT_REWARDS_ORDER_HISTORY_FILTER);
      getRewardsOrdersList();
    }
  }, [company, isMounted]);

  return (
    <div className="page-container curved" data-experience="new">
      <RewardsPageHeader
        breadcrumbs={loadBalanceBreadcrumbs}
        rewardsLoading={rewardsLoading}
        rewardsError={rewardsError}
        currency={currency}
        balance={balance}
        title={t(TRANSLATION_HISTORY.header.title)}
        description={t(TRANSLATION_HISTORY.header.description)}
      />
      <PageCurvedContainer>
        {rewardsError ? (
          <CustomEmptyPage
            title={t(TRANSLATIONS_SETTINGS.error.title)}
            subtitle={t(TRANSLATIONS_SETTINGS.error.subtitle)}
            newExperience={true}
          />
        ) : (
          <>
            <RewardsHistoryFilter
              loading={loading}
              ordersFilters={rewardsOrdersFilters}
              changeRewardsOrdersFilters={changeRewardsOrdersFilters}
            />
            <RewardsHistoryTable
              error={error}
              loading={loading}
              ordersList={orders}
              orderType={rewardsOrdersFilters.type.value}
            />
          </>
        )}
      </PageCurvedContainer>
    </div>
  );
};

export default RewardsHistory;
